import { createSlice } from '@reduxjs/toolkit';

import {
    createPatientFlag,
    deletePatientFlag,
    fetchPatientFlags,
    PATIENT_FLAGS_PAGE_LIMIT,
    setPatientFlagAssignment,
    updatePatientFlag
} from './patient-flags-api';
import type { PatientFlagsState } from './types';
import { logout } from '../auth/logout';

export {
    createPatientFlag,
    deletePatientFlag,
    fetchPatientFlags,
    PATIENT_FLAGS_PAGE_LIMIT,
    setPatientFlagAssignment,
    updatePatientFlag
} from './patient-flags-api';
export type {
    CreatePatientFlagArg,
    DeletePatientFlagArg,
    FetchPatientFlagsArg,
    FetchPatientFlagsResult,
    PatientFlagsRejectValue,
    SetPatientFlagAssignmentArg,
    SetPatientFlagAssignmentResult,
    UpdatePatientFlagArg
} from './patient-flags-api';
export type { PatientFlagsState } from './types';

const initialState: PatientFlagsState = {
    definitions: [],
    definitionsPagination: null,
    definitionsCacheKey: null,
    definitionsLoading: false,
    definitionsError: null,
    mutateLoading: false,
    mutateError: null,
    assignmentLoadingPatientId: null,
    assignmentError: null
};

const patientFlagsSlice = createSlice({
    name: 'patientFlags',
    initialState,
    reducers: {
        clearPatientFlags: () => initialState,
        clearPatientFlagsError: (state) => {
            state.definitionsError = null;
            state.mutateError = null;
            state.assignmentError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPatientFlags.pending, (state) => {
                state.definitionsLoading = true;
                state.definitionsError = null;
            })
            .addCase(fetchPatientFlags.fulfilled, (state, action) => {
                state.definitionsLoading = false;
                state.definitions = action.payload.items;
                state.definitionsPagination = action.payload.pagination ?? null;
                state.definitionsCacheKey = [
                    action.meta.arg?.hospitalId ?? 'default',
                    action.meta.arg?.search?.trim() ?? '',
                    String(action.meta.arg?.offset ?? 0),
                    String(action.meta.arg?.limit ?? PATIENT_FLAGS_PAGE_LIMIT)
                ].join('|');
                state.definitionsError = null;
            })
            .addCase(fetchPatientFlags.rejected, (state, { payload, error }) => {
                state.definitionsLoading = false;
                if (error.name === 'AbortError') {
                    state.definitionsError = null;
                    return;
                }
                state.definitionsError = payload?.message ?? 'Failed to load patient flags';
            })
            .addCase(createPatientFlag.pending, (state) => {
                state.mutateLoading = true;
                state.mutateError = null;
            })
            .addCase(createPatientFlag.fulfilled, (state, action) => {
                state.mutateLoading = false;
                state.mutateError = null;
                state.definitions = [...state.definitions, action.payload];
                state.definitionsCacheKey = null;
            })
            .addCase(createPatientFlag.rejected, (state, { payload }) => {
                state.mutateLoading = false;
                state.mutateError = payload?.message ?? 'Failed to create patient flag';
            })
            .addCase(updatePatientFlag.pending, (state) => {
                state.mutateLoading = true;
                state.mutateError = null;
            })
            .addCase(updatePatientFlag.fulfilled, (state, action) => {
                state.mutateLoading = false;
                state.mutateError = null;
                state.definitions = state.definitions.map((flag) => (flag.id === action.payload.id ? action.payload : flag));
                state.definitionsCacheKey = null;
            })
            .addCase(updatePatientFlag.rejected, (state, { payload }) => {
                state.mutateLoading = false;
                state.mutateError = payload?.message ?? 'Failed to update patient flag';
            })
            .addCase(deletePatientFlag.pending, (state) => {
                state.mutateLoading = true;
                state.mutateError = null;
            })
            .addCase(deletePatientFlag.fulfilled, (state, action) => {
                state.mutateLoading = false;
                state.mutateError = null;
                state.definitions = state.definitions.filter((flag) => flag.id !== action.payload.id);
                state.definitionsCacheKey = null;
            })
            .addCase(deletePatientFlag.rejected, (state, { payload }) => {
                state.mutateLoading = false;
                state.mutateError = payload?.message ?? 'Failed to delete patient flag';
            })
            .addCase(setPatientFlagAssignment.pending, (state, action) => {
                state.assignmentLoadingPatientId = action.meta.arg.patientId;
                state.assignmentError = null;
            })
            .addCase(setPatientFlagAssignment.fulfilled, (state, action) => {
                state.assignmentLoadingPatientId = null;
                state.assignmentError = null;
                const { flagId, body } = action.meta.arg;
                const definition = state.definitions.find((flag) => flag.id === flagId);
                if (definition) {
                    const current = typeof definition.usageCount === 'number' ? definition.usageCount : 0;
                    const delta = body.assigned ? 1 : -1;
                    definition.usageCount = Math.max(0, current + delta);
                }
                state.definitionsCacheKey = null;
            })
            .addCase(setPatientFlagAssignment.rejected, (state, { payload }) => {
                state.assignmentLoadingPatientId = null;
                state.assignmentError = payload?.message ?? 'Failed to update patient flag assignment';
            })
            .addCase(logout, () => initialState);
    }
});

export const { clearPatientFlags, clearPatientFlagsError } = patientFlagsSlice.actions;
export default patientFlagsSlice.reducer;
